import { useObserva } from '@morganisbatman/observa-client-react'
import { useEffect, useState } from 'react'
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from 'react-native'
import { ErrorNotice } from '../components/ErrorNotice'
import { FulfilmentScreen } from '../components/FulfilmentScreen'
import { POLL_TIMEOUT, pollTimeoutPayload } from '../lib/paywall-events'
import { colors, fontWeight, radius } from '../tokens'
import { trpc } from '../trpc'

type Props = {
  appealId: string
  onDismiss: () => void
}

// Webhook latency target: Stripe usually fires checkout.session.completed
// within ~2s of the customer landing on success_url, but flaky networks can
// push it out. Poll every 2s for up to 60s before showing a "still
// processing" fallback.
const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 60_000

export const PostPaymentScreen = ({ appealId, onDismiss }: Props) => {
  const [pollExpired, setPollExpired] = useState(false)
  const startedAt = useState(() => Date.now())[0]
  const observa = useObserva()

  // Beacon: the webhook didn't flip paidAt within 60s and the user is staring
  // at "still processing" — a post-payment anxiety signal worth quantifying.
  useEffect(() => {
    if (!pollExpired) return
    void observa
      .event({ eventType: POLL_TIMEOUT, payload: pollTimeoutPayload(appealId) })
      .catch(() => {})
  }, [pollExpired, observa, appealId])

  const status = trpc.appeal.getById.useQuery(
    { id: appealId },
    {
      refetchInterval: (query) => {
        if (query.state.data?.paidAt) return false
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setPollExpired(true)
          return false
        }
        return POLL_INTERVAL_MS
      },
    },
  )

  if (status.isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary[700]} />
      </View>
    )
  }

  if (status.error) {
    return (
      <View style={styles.centered}>
        <ErrorNotice error={status.error} onRetry={() => status.refetch()} />
        <Text style={styles.subtle} accessibilityRole="link" onPress={onDismiss}>
          Back to home
        </Text>
      </View>
    )
  }

  if (!status.data?.paidAt) {
    return (
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.pending}>
          <ActivityIndicator color={colors.primary[700]} />
          <Text style={styles.pendingTitle}>Payment received — finalising</Text>
          <Text style={styles.pendingSub}>
            Stripe is confirming your payment. This usually takes a couple of seconds.
          </Text>
          {pollExpired ? (
            <Text style={styles.subtle}>
              Still processing — your appeal pack will be ready once Stripe confirms. Refresh this
              page in a minute, or check your email for the receipt.
            </Text>
          ) : null}
        </View>
      </ScrollView>
    )
  }

  return (
    <ScrollView contentContainerStyle={styles.scroll}>
      <FulfilmentScreen
        appealId={status.data.id}
        priceAmountPence={status.data.priceAmountPence}
        receiptUrl={status.data.stripeReceiptUrl}
        qualifiesForFormalProposal={status.data.qualifiesForFormalProposal}
        onCheckAnother={onDismiss}
      />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  scroll: { padding: 24, alignItems: 'center', backgroundColor: colors.bg, minHeight: '100%' },
  centered: { padding: 48, alignItems: 'center', gap: 12, backgroundColor: colors.bg },
  pending: {
    marginTop: 48,
    padding: 22,
    borderRadius: radius.lg,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    gap: 10,
    alignItems: 'flex-start',
    maxWidth: 480,
    width: '100%',
  },
  pendingTitle: { fontSize: 17, fontWeight: fontWeight.bold, color: colors.ink },
  pendingSub: { fontSize: 14, color: colors.muted, lineHeight: 21 },
  subtle: { fontSize: 13, color: colors.muted, marginTop: 4 },
  error: { color: colors.error[700], fontSize: 14 },
})
